import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { verifyPin, unlock } from "../../lib/vaultLock";
import { tapLight, tapSuccess, tapError } from "../../lib/haptics";
import { soundTick, soundSuccess, soundError } from "../../lib/sound";

/**
 * Vault PIN entry screen. The entire visual (TipLingo wordmark, shield,
 * "Document Vault" title, dot row, numeric keypad with Cancel/0/⌫ on the
 * bottom row, and "PIN stays on this device" footer) is baked into a
 * single 3D mockup that lives at:
 *
 *   /public/assets/vault-icons/vault-keypad-locked.png   (5:9 aspect)
 *
 * We layer the image full-bleed and overlay transparent buttons on top
 * of each keypad position so taps still drive the existing PIN logic.
 * After the 4th digit verifies, we hue-rotate the same artwork toward
 * green for 1s before handing off to `onUnlocked()` — that intentional
 * pause lets the user actually see the success state.
 *
 * Hit-zone percentages were measured against the source artwork's
 * 1290×2316 (~5:9) aspect ratio and stay pixel-aligned across viewport
 * sizes because the wrapper preserves that ratio in CSS.
 */
const PIN_LENGTH = 4;
const UNLOCK_HOLD_MS = 1000;

// Approximate keypad/footer coordinates as a % of the redesigned 1290×2316
// artwork (5:9). Cancel now lives inside the 3×4 keypad grid (bottom-left),
// replacing the previous standalone underlined text link.
const KEYPAD = { left: 22.5, top: 51, width: 55, height: 37 };

// The 4 painted dots in the artwork sit at these exact x-centers and y-center
// (measured pixel-by-pixel against the 1290×2316 source PNG, verified against
// a live calibration overlay). We position our overlay dots absolutely so
// they overlap the painted outlines instead of drifting toward center with a
// generic flex+gap layout.
const DOT_CENTERS = [33.3, 44.3, 55.4, 66.5];
const DOT_Y_CENTER = 46.7; // % of artwork height — calibrated against
                           //  painted outlines on real mobile Safari rendering
const DOT_DIAMETER = 3.2; // % of artwork width — sized larger than the
                          //  painted outlines so the neon fill covers them
                          //  cleanly even with sub-pixel rendering drift

export default function VaultLockScreen({ onUnlocked, onBack }) {
  const [digits, setDigits] = useState("");
  const [shaking, setShaking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const verifyingRef = useRef(false);

  function press(d) {
    if (verifyingRef.current || unlocked) return;
    // Light keypad tick on iOS — matches Apple's native PIN UX.
    tapLight();
    soundTick();
    setDigits((cur) => (cur.length >= PIN_LENGTH ? cur : cur + d));
  }
  function back() {
    if (verifyingRef.current || unlocked) return;
    tapLight();
    soundTick();
    setDigits((cur) => cur.slice(0, -1));
  }

  useEffect(() => {
    if (digits.length !== PIN_LENGTH) return;
    verifyingRef.current = true;
    (async () => {
      const ok = await verifyPin(digits);
      if (ok) {
        unlock();
        setUnlocked(true);
        tapSuccess(); // Soft success tick — confirms unlock on iOS.
        soundSuccess(); // Web-Audio cue — works everywhere as fallback.
        // Hold the green-glow success state briefly so the user actually
        // sees confirmation before the Vault grid replaces the screen.
        setTimeout(() => {
          onUnlocked?.();
        }, UNLOCK_HOLD_MS);
      } else {
        tapError(); // Sharp error buzz — matches Face ID failure feedback.
        soundError(); // Audible denial cue (square-wave low buzz).
        setShaking(true);
        setTimeout(() => {
          setShaking(false);
          setDigits("");
          verifyingRef.current = false;
        }, 450);
        toast.error("Incorrect PIN");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  const artworkSrc = "/assets/vault-icons/vault-keypad-locked.png";

  // The vault lock screen is rendered via a Portal to <body>. AppShell
  // wraps everything in `<main className="animate-fade-up">`, and that
  // keyframe keeps a `transform: translateY(0)` declared on the element
  // permanently (fill-mode: both). A non-`none` transform turns its
  // ancestor into the containing block for `position: fixed` children,
  // which would collapse our `inset-0` fullscreen overlay back into the
  // 448px `max-w-md` column. Escaping to body sidesteps that entirely.
  const overlay = (
    <div
      className={`fixed inset-0 z-50 overflow-hidden bg-bone vault-lock-bg ${
        shaking ? "animate-vault-shake" : ""
      }`}
      data-testid="vault-lock-screen"
      data-vault-state={unlocked ? "unlocked" : "locked"}
      style={{
        // The 3D vault-door artwork is rendered as a full-bleed
        // background so the browser handles fit math natively.
        // The CSS below switches between cover (portrait/mobile) and
        // contain (landscape/desktop) so every keypad button stays
        // tappable regardless of the device aspect ratio.
        backgroundImage: `url(${artworkSrc})`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#FAF8F1",
      }}
    >
      {/* Success state: a hue-rotated copy of the artwork is layered on top
          with the top 15% clipped away, so the TipLingo wordmark keeps its
          original blue/orange colors while the red shield-glow + "Vault
          locked" status shift toward green for the 1-second unlock hold. */}
      {unlocked && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none vault-lock-unlocked-overlay"
          style={{
            backgroundImage: `url(${artworkSrc})`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
      )}
      {/* Invisible coordinate-space box that matches the artwork's 2:3
          aspect and the SAME fit math as the background above. Hit-zones
          inside this box are positioned in % of the artwork, so they
          stay pixel-aligned with the painted keypad on every device. */}
      <div
        className="vault-lock-hitbox absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none"
      >
        {/* 3D artwork is rendered as the parent's background-image so the
            browser handles cover-fit natively (see comment on the outer
            container). This empty inner div carries only the hit-zone
            geometry. */}

        {/* PIN progress dots — overlaid exactly on top of the painted dot
            row in the artwork so each typed digit lights up live. Positions
            were measured against the source PNG so they stay pixel-aligned
            across viewport sizes (the hitbox preserves the artwork aspect).
            Hidden while the unlock animation runs to avoid stacking with
            any decorative state. */}
        {!unlocked &&
          DOT_CENTERS.map((cx, i) => {
            // CSS aspect-square means height = width-in-pixels, but the
            // hitbox is portrait, so 2.4% of width is a smaller % of height.
            // Convert so the dot is vertically centered on the painted dot.
            const dotHpct = DOT_DIAMETER * (1290 / 2316);
            return (
              <span
                key={i}
                className="absolute rounded-full transition-all duration-150"
                data-testid={`vault-pin-dot-${i}`}
                style={{
                  left: `${cx - DOT_DIAMETER / 2}%`,
                  top: `${DOT_Y_CENTER - dotHpct / 2}%`,
                  width: `${DOT_DIAMETER}%`,
                  aspectRatio: "1 / 1",
                  background:
                    digits.length > i
                      ? "#00E5FF" // neon blue fill
                      : "transparent",
                  boxShadow:
                    digits.length > i
                      ? "0 0 8px #00E5FF, 0 0 18px rgba(0, 229, 255, 0.55)"
                      : "none",
                }}
              />
            );
          })}

        {/* Transparent hit-grid sitting on top of the 3D keypad. The
            buttons are visually invisible (no bg, no border, no text
            colour) but capture taps so the existing PIN logic runs. */}
        <div
          className="absolute"
          style={{
            left: `${KEYPAD.left}%`,
            top: `${KEYPAD.top}%`,
            width: `${KEYPAD.width}%`,
            height: `${KEYPAD.height}%`,
          }}
        >
          <div className="grid grid-cols-3 grid-rows-4 gap-[4%] w-full h-full">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
              <HitButton
                key={n}
                onPress={() => press(String(n))}
                label={String(n)}
                testId={`vault-key-${n}`}
              />
            ))}
            {onBack ? (
              <HitButton
                onPress={onBack}
                label="Cancel"
                testId="vault-lock-cancel"
              />
            ) : (
              <span />
            )}
            <HitButton
              onPress={() => press("0")}
              label="0"
              testId="vault-key-0"
            />
            <HitButton
              onPress={back}
              label="Backspace"
              testId="vault-key-back"
            />
          </div>
        </div>
      </div>

      <style>{`
        /* Portrait viewports (mobile): cover-fit so the door reaches
           every edge — no bone gutters. The hit-zone coordinate box
           grows past the viewport, the keypad stays roughly centered,
           and the keypad row at 51%-88% of the artwork stays well
           within the visible area for any phone aspect <= 1. */
        .vault-lock-bg { background-size: cover; }
        .vault-lock-hitbox {
          width: max(100vw, calc(100vh * 1290 / 2316));
          height: max(100vh, calc(100vw * 2316 / 1290));
        }

        /* Successful unlock: stack a hue-rotated copy of the artwork on top,
           with the top 15% clipped away so the TipLingo wordmark keeps its
           original blue/orange colors. The red shield-glow and "Vault
           locked" status text underneath get tinted green for the 1s hold
           before the vault grid takes over. */
        .vault-lock-unlocked-overlay {
          background-size: cover;
          filter: hue-rotate(105deg) saturate(0.95) brightness(1.02);
          clip-path: inset(13% 0 0 0);
          animation: vault-unlock-fade 220ms ease-out;
        }
        @keyframes vault-unlock-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* Landscape viewports (desktop, tablet rotated): switch to
           contain-fit so the entire door — including the bottom Cancel /
           0 / backspace row — is always visible. The bone background
           shows at the sides instead of cropping the keypad off-screen. */
        @media (min-aspect-ratio: 1 / 1) {
          .vault-lock-bg { background-size: contain; }
          .vault-lock-unlocked-overlay { background-size: contain; }
          .vault-lock-hitbox {
            width: min(100vw, calc(100vh * 1290 / 2316));
            height: min(100vh, calc(100vw * 2316 / 1290));
          }
        }

        @keyframes vault-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
        .animate-vault-shake { animation: vault-shake 0.42s ease-in-out; }
      `}</style>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(overlay, document.body)
    : overlay;
}

function HitButton({ onPress, label, testId }) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={label}
      data-testid={testId}
      className="w-full h-full rounded-full bg-transparent active:scale-95 transition-transform"
    />
  );
}
